<template>
  <v-page white>
    <toolbar v-if="!disableHeader" />

    <div class="container">
      <message-thread-prompt
        v-if="!isAuthenticated && hasCheckedAuthentication"
        heading="Sign in to start chatting!"
        to="#auth"
        button-text="Sign in"
      />

      <message-thread-prompt
        v-else-if="disableMessagePage"
        heading="Messaging not available on this app"
      />

      <message-thread-prompt
        v-else-if="isFailed"
        icon="disabled_by_default"
        heading="Messages failed to load"
      />

      <div v-else-if="isLoading || !isFetched">
        <placeholder-rect class="mb-3" />
        <placeholder-rect width="25%" height="16px" class="mb-1" />
        <div class="d-flex w-100">
          <placeholder-rect round width="54px" height="44px" class="mr-1" />
          <div class="w-100">
            <placeholder-rect class="ml-1 mb-1" height="18px" />
            <placeholder-rect class="ml-1 mt-1" height="16px" width="40%" />
          </div>
        </div>
        <div class="d-flex w-100">
          <placeholder-rect round width="54px" height="44px" class="mr-1" />
          <div class="w-100">
            <placeholder-rect class="ml-1 mb-1" height="18px" />
            <placeholder-rect class="ml-1 mt-1" height="16px" width="40%" />
          </div>
        </div>
      </div>

      <div v-else-if="isAuthenticated" class="thread-list pb-3">
        <template v-if="privateChatEnabled">
          <new-conversation :is-admin="isAdmin" />
        </template>
        <template v-else>
          <div class="mt-3" />
        </template>

        <template v-if="messageThreads.length > 0">
          <h4 class="thread-list__heading px-2 mt-0 mb-1">All Messages</h4>
          <div class="thread-list__main" ref="message-threads-wrapper">
            <thread-tab
              v-for="messageThread in orderedMessageThreads"
              :key="messageThread.id"
              :message-thread="messageThread"
              :unread="messageThread.is_unread"
            />

            <div
              v-if="isLoadingMore"
              class="d-flex justify-content-center opacity-40 mt-2"
            >
              <v-loader />
            </div>
          </div>

          <div v-if="noMoreThreads" class="no-more text-center mt-5">
            No more messages
          </div>

          <div ref="load-observer" style="height: 2px; margin-top: 50px"></div>
        </template>

        <message-thread-prompt
          v-if="messageThreads.length === 0"
          :heading="emptyChatHeading"
          :description="emptyChatDescription"
          padding-top="10vh"
        />
      </div>
    </div>
  </v-page>
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from "vuex"

import { WINDOW_EVENTS } from "@shared/admin-app-constants"

import { subscribeToThread } from "@/lib/pusher-utils"
import isPreviewContext from "@/lib/is-platform/is-preview-context"

import PlaceholderRect from "@/components/PlaceholderRect"
import Toolbar from "@/components/Toolbar"
import ThreadTab from "@/components/message-thread/ThreadTab"
import MessageThreadPrompt from "@/components/message-thread/Prompt"
import NewConversation from "@/components/message-thread/NewConversation"

export default {
  components: {
    Toolbar,
    ThreadTab,
    MessageThreadPrompt,
    NewConversation,
    PlaceholderRect
  },

  data() {
    return {
      isLoading: false,
      isLoadingMore: false,
      noMoreThreads: false,
      isFailed: false,
      isFetched: false,
      disableHeader: isPreviewContext(),
      page: 1
    }
  },

  computed: {
    ...mapState("auth", ["user", "hasCheckedAuthentication"]),
    ...mapGetters(["subscription", "trial", "privateChatEnabled", "app"]),
    ...mapGetters("messages", [
      "getMessageThread",
      "getMessageThreads",
      "hasUnreadMessageThreads",
      "isUnreadAt"
    ]),
    ...mapGetters("auth", ["isAuthenticated"]),

    messageThreads() {
      return this.getMessageThreads.filter(thread =>
        thread.is_feed ? false : thread.is_in_thread && !thread.is_reply
      )
    },

    orderedMessageThreads() {
      return this.sortMessageThread(this.messageThreads)
    },

    isAdmin() {
      return !!this.user.isAccountAdmin
    },

    disableMessagePage() {
      return (
        (!this.subscription ||
          !!this.subscription.is_free ||
          !this.subscription.active) &&
        !this.trial?.active
      )
    },

    isPro() {
      return this.subscription?.is_pro
    },

    emptyChatHeading() {
      return !this.isAdmin || this.isPro
        ? "No messages yet!"
        : "Private Message"
    },

    emptyChatDescription() {
      return !this.isAdmin
        ? `Start a one-to-one chat with ${this.app.name} or join a chat within the app`
        : this.isPro
        ? "Start a conversation with any member of your app by using the search box above." +
          "\n\n To start a private group message, select multiple members from the search box above."
        : "Start a conversation with any member of your app by using the search box above."
    }
  },

  watch: {
    isAuthenticated: {
      immediate: true,
      handler: function() {
        if (this.isAuthenticated) {
          this.initialize()
        }
      }
    }
  },

  methods: {
    ...mapMutations("messages", ["removeParticipantFromThread"]),
    ...mapActions("messages", [
      "fetchMessageThreads",
      "fetchMessageThread",
      "setMessageThread",
      "removeMessageThread"
    ]),

    initialize() {
      this.subscribeToGlobalEventsChannel()
      this.fetchThreads()
    },

    fetchThreads() {
      if (!this.isAuthenticated || this.disableMessagePage) return

      this.isLoading = true

      this.fetchMessageThreads()
        .then(() => {
          this.page = 1 // Reset page when navigate back from message thread show pages
          this.isLoading = false
          this.isFetched = true

          this.$nextTick(() => {
            if (this.messageThreads.length > 0) this.setupInfiniteLoadObserver()
          })
        })
        .catch(err => {
          console.error(err)

          this.isLoading = false
          this.isFailed = true
        })
    },

    setupInfiniteLoadObserver() {
      const vm = this
      const observer = new IntersectionObserver(entries => {
        if (vm.noMoreThreads) observer.unobserve(this.$refs["load-observer"])

        if (entries[0].isIntersecting) this.loadMoreThreads()
      })

      observer.observe(this.$refs["load-observer"])
    },

    loadMoreThreads() {
      if (this.isLoadingMore) return

      this.isLoadingMore = true

      this.fetchMessageThreads(this.page + 1)
        .then(threads => {
          this.isFetched = true

          if (threads.length === 0) {
            this.noMoreThreads = true
          }

          this.page += 1
        })
        .finally(() => {
          this.isLoadingMore = false
        })
    },

    subscribeToGlobalEventsChannel() {
      subscribeToThread(
        this.user.globalEventsChannel,
        {
          onMessage: this.handleNewMessage,
          onParticipantRemoved: this.handleRemoveParticipant
        },
        false
      )
    },

    handleNewMessage(pusherData) {
      const messageThread = this.getMessageThread(pusherData.messageThreadId)
      const latestMessageData = {
        message_type: pusherData.messageType,
        message: pusherData.messageBody,
        image_urls: pusherData.imageUrls,
        message_ts: pusherData.messageCreatedAt,
        user: pusherData.user
      }

      if (messageThread) {
        this.setMessageThread({
          ...messageThread,
          ...{
            is_unread: this.isUnreadAt(
              messageThread.id,
              pusherData.messageCreatedAt
            ),
            latest_message: {
              ...messageThread.latest_message,
              ...latestMessageData
            }
          }
        })
      } else {
        this.fetchMessageThread(pusherData.messageThreadId)
      }

      if (isPreviewContext()) {
        const { postMessage } = require("@/lib/admin-iframe-helpers")

        postMessage(WINDOW_EVENTS.UPDATE_UNREAD_STATUS, {
          hasUnread: this.hasUnreadMessageThreads
        })
      }
    },

    handleRemoveParticipant(pusherData) {
      if (!this.isAdmin && this.user?.id == pusherData.userId) {
        this.removeMessageThread(pusherData.messageThreadId)
      } else {
        this.removeParticipantFromThread({
          messageThreadId: pusherData.messageThreadId,
          title: pusherData.messageThreadTitle,
          userId: pusherData.userId
        })
      }
    },

    sortMessageThread(arr) {
      return arr.slice().sort((prev, curr) => {
        const currTs = curr.latest_message_ts
        const prevTs = prev.latest_message_ts

        if (currTs === null || currTs === undefined) return -1
        if (prevTs === null || prevTs === undefined) return 1

        return prevTs < currTs ? 1 : -1
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";

.thread-list {
  &__header {
    display: flex;
    align-items: center;
    width: 100%;
    min-height: 4rem;
    padding: 0.75rem;
    top: 0;
    box-shadow: $box-shadow--medium;
    z-index: 1000;

    .material-icons-round {
      color: $color-ui--grey-70;
      cursor: pointer;
    }
  }

  &__heading {
    font-size: 15px;
    font-weight: 700;
  }
}
</style>
